import { format } from 'date-fns';
import { calculateRange } from 'src/@core/utils/getDateRange';

/**
 * Calculate the combined total spaces for a given collection of sales orders
 *
 * @param data any[]
 * @returns number
 */
export const calculateTotalSpaces = (data: any[]): number => {
  return data.reduce((acc, curr) => acc + curr.totalSpaces, 0);
};

/**
 * Calculate the combined total value for a given collection of sales orders
 *
 * @param data any[]
 * @returns number
 */
export const calculateTotalValue = (data: any[]): number => {
  return data.reduce((acc, curr) => acc + Number.parseFloat(curr.price), 0);
};

/**
 * Calculate average value per pallet space
 *
 * @param totalValue number
 * @param totalSpaces number
 * @returns string
 */
export const calculateAverageValue = (totalValue: number, totalSpaces: number): string => {
  if (totalSpaces === 0 || totalValue === 0) {
    return (0).toFixed(2);
  }

  return (totalValue / totalSpaces).toFixed(2);
};

/**
 * Calculate the difference between two averages
 * - returns difference value and percentage
 *
 * @param initialAverage string
 * @param averageForRange string
 * @returns
 */
export const calculateDifference = (
  initialAverage: string,
  averageForRange: string
): { difference: number; percentage: number } => {
  const now = Number(initialAverage);
  const previous = Number(averageForRange);

  if (previous === 0) {
    return {
      difference: now > 0 ? now : 0,
      percentage: 100
    };
  }

  const difference = now - previous;

  let percentage = ((now - previous) / previous) * 100;

  if (isNaN(percentage)) {
    percentage = 0;
  }

  return {
    difference,
    percentage
  };
};

/**
 * Calculate totals (spaces & value) and average cost per pallet space for a given collection of sales orders
 *
 * @param data any[]
 * @returns {totalSpaces: number, totalValue: number, average: string}
 */
export const calculateTotals = (data: any[]): { totalSpaces: number; totalValue: number; average: string } => {
  const spaces = calculateTotalSpaces(data);
  const value = calculateTotalValue(data);
  const average = calculateAverageValue(value, spaces);

  return {
    totalSpaces: spaces,
    totalValue: value,
    average
  };
};

/**
 * Get comparison filter string
 *
 * @param orderByValue string
 * @param requiredRange string
 * @returns string | undefined
 */
export const getComparisonFilterString = (orderByValue: string, requiredRange: string): string | undefined => {
  if (!requiredRange) return;

  const { dateFrom, dateTo } = calculateRange(requiredRange, 0);
  dateFrom?.setHours(dateFrom.getHours() + 1);

  return `${orderByValue} ge datetime'${dateFrom?.toISOString()}' and ${orderByValue} le datetime'${dateTo?.toISOString()}'`;
};

/**
 * Get colour for trend
 *
 * @param value number
 * @return string
 */
export const getColour = (value: number): string => {
  if (value > 0) return TrendColours.Positive;
  if (value < 0) return TrendColours.Negative;

  return TrendColours.Neutral;
};

/**
 * Get rotation angle based on trend of value
 *
 * @param value number
 * @returns string
 */
export const getRotationAngle = (value: number): string => {
  if (value > 0) return TrendRotationAngles.Positive;
  if (value < 0) return TrendRotationAngles.Negative;

  return TrendRotationAngles.Neutral;
};

/**
 * Get text for trend
 *
 * @param value number
 * @param percentage number
 * @param range string
 * @returns string
 */
export const getText = (value: number, percentage: number, range: string): string => {
  const { dateFrom = new Date(), dateTo = new Date() } = calculateRange(range, 0);

  if (value > 0) {
    return `Up ${percentage.toFixed(2)}% VS <abbr title="${format(dateFrom as Date, 'dd/MM/yyyy')} - ${format(
      dateTo as Date,
      'dd/MM/yyyy'
    )}">${range}</abbr>`;
  }

  if (value < 0) {
    return `Down ${percentage.toFixed(2)}% VS <abbr title="${format(dateFrom as Date, 'dd/MM/yyyy')} - ${format(
      dateTo as Date,
      'dd/MM/yyyy'
    )}">${range}</abbr>`;
  }

  return ``;
};

/**
 * Trend colours enum
 */
export enum TrendColours {
  Positive = '#32CD32',
  Neutral = '#acacac',
  Negative = '#E30613'
}

/**
 * Trend rotation angles enum
 */
export enum TrendRotationAngles {
  Positive = '0deg',
  Neutral = '90deg',
  Negative = '180deg'
}

/**
 * Allowed Ranges
 */
export const allowedRanges: string[][] = [
  ['Today', 'Yesterday'],
  ['This Week', 'Last Week'],
  ['Last Week', 'Week Before Last'],
  ['This Month', 'Last Month'],
  ['Last 3 Months', 'Previous 3 Months'],
  ['Last 2 Weeks', 'Previous 2 Week Period'],
  ['Last 3 Weeks', 'Previous 3 Week Period'],
  ['Last 4 Weeks', 'Previous 4 Week Period']
];
